import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import type { Session } from '../modal/session';
import type {
  CreateSessionParams,
  CreateSessionResponse,
} from './create-session';

export const ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY =
  'enrollio-active-session-rollover-job';

export type RolloverDateMode = 'shift' | 'snapToSession';
export type RolloverEnrollmentDisposition = 'history' | 'delete' | 'keep';
export type RolloverMode = 'copy' | 'move';

export interface SessionRolloverFutureFees {
  costumeFee?: number;
  classRegistrationFeeAmount?: number;
  sessionRegistrationPerStudent?: Record<string, number>;
}

export interface SessionRolloverFeesInput {
  sessionRegistration: boolean;
  classRegistration: boolean;
  costume: boolean;
  costumeAmount?: number;
  chargeSessionRegistrationForContinuing?: boolean;
  chargeClassRegistrationForContinuing?: boolean;
  futureFees?: SessionRolloverFutureFees;
}

export interface SessionRolloverBillingInput {
  resetExistingSubscriptions: boolean;
  termStartDate?: string;
  termEndDate?: string;
}

export const getSessionRolloverErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => {
  if (!isAxiosError(error)) {
    return fallbackMessage;
  }

  const responseData = error.response?.data;
  if (typeof responseData === 'string' && responseData.trim().length > 0) {
    return responseData;
  }

  if (responseData && typeof responseData === 'object') {
    const nestedError = (
      responseData as { error?: string | { message?: unknown } }
    ).error;
    if (typeof nestedError === 'string' && nestedError.trim().length > 0) {
      return nestedError;
    }
    if (
      nestedError &&
      typeof nestedError === 'object' &&
      typeof nestedError.message === 'string' &&
      nestedError.message.trim().length > 0
    ) {
      return nestedError.message;
    }

    const message = (responseData as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallbackMessage;
};

export const isMissingSessionRolloverJobError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

export interface SessionRolloverClassPreview {
  id: string;
  title: string;
  sourceStartDate?: string;
  sourceEndDate?: string;
  proposedStartDate?: string;
  proposedEndDate?: string;
  eligibleStudents: number;
  hourly: boolean;
  weekdayWarning: boolean;
  registrationFeeAmount: number;
  costumeFee: number;
}

export interface SessionRolloverPreview {
  deltaDays: number;
  destinationClassCount: number;
  blockingRollover?: {
    jobId: string;
    status: SessionRolloverJobStatus;
  } | null;
  allClassIds: Array<string>;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  classes: Array<SessionRolloverClassPreview>;
}

export interface V2SessionsResponse {
  sessions: Array<Session>;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type SessionRolloverJobStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled'
  | 'deleted';

export interface SessionRolloverJob {
  jobId: string;
  sourceSessionId: string;
  destinationSessionId: string;
  status: SessionRolloverJobStatus;
  phase: string;
  totalUnits: number;
  processedUnits: number;
  progress: number;
  currentItem?: string | null;
  errorMessage?: string | null;
  events: Array<{
    at: string;
    phase: string;
    message: string;
    level: 'info' | 'warn' | 'error';
  }>;
  errors: Array<{ scope: string; id?: string; message: string }>;
  canRetry: boolean;
  canDelete: boolean;
  createdAt: string;
  completedAt?: string | null;
}

export interface StartSessionRolloverInput {
  sourceSessionId: string;
  destinationSessionId: string;
  destinationSessionCreatedInWizard: boolean;
  classIds: Array<string>;
  dateMode: RolloverDateMode;
  enrollmentDisposition: RolloverEnrollmentDisposition;
  classDateOverrides?: Array<{
    classId: string;
    startDate: string;
    endDate: string;
  }>;
  fees: SessionRolloverFeesInput;
  mode?: RolloverMode;
  billing?: SessionRolloverBillingInput;
}

const rolloverKey = (suffix?: string) => [
  'session-rollovers',
  ...(suffix ? [suffix] : []),
];

export const useSessionRolloverPreview = (params: {
  sourceSessionId: string;
  destinationSessionId: string;
  dateMode: RolloverDateMode;
  page: number;
  limit: number;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: rolloverKey(
      `preview-${params.sourceSessionId}-${params.destinationSessionId}-${params.dateMode}-${params.page}-${params.limit}`,
    ),
    queryFn: async () => {
      const response = await baseAPI.get<SessionRolloverPreview>(
        '/v2/session-rollovers/preview',
        {
          // The API rejects unknown query params, so only send the filters.
          params: {
            sourceSessionId: params.sourceSessionId,
            destinationSessionId: params.destinationSessionId,
            dateMode: params.dateMode,
            page: params.page,
            limit: params.limit,
          },
        },
      );
      return response.data;
    },
    enabled:
      params.enabled !== false &&
      Boolean(params.sourceSessionId) &&
      Boolean(params.destinationSessionId) &&
      params.sourceSessionId !== params.destinationSessionId,
  });

export const useV2RolloverSessions = (params: {
  name?: string;
  limit?: number;
  includeArchivedSessions?: boolean;
}) =>
  useQuery({
    queryKey: rolloverKey(
      `sessions-${params.name ?? ''}-${params.limit ?? 50}-${Boolean(
        params.includeArchivedSessions,
      )}`,
    ),
    queryFn: async () => {
      const response = await baseAPI.get<V2SessionsResponse>('/v2/sessions', {
        params: {
          page: 1,
          limit: params.limit ?? 50,
          name: params.name,
          includeArchivedSessions: params.includeArchivedSessions,
        },
      });
      return response.data;
    },
  });

export const useV2RolloverSession = (
  sessionId: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: rolloverKey(`session-${sessionId}`),
    queryFn: async () => {
      const response = await baseAPI.get<Session>(`/v2/sessions/${sessionId}`);
      return response.data;
    },
    enabled: options?.enabled !== false && Boolean(sessionId),
  });

export const useCreateV2RolloverSession = () =>
  useMutation({
    mutationFn: async (input: CreateSessionParams) => {
      const response = await baseAPI.post<CreateSessionResponse>(
        '/v2/sessions',
        input,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: rolloverKey(),
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.SESSIONS],
      });
    },
  });

export const useStartSessionRollover = () =>
  useMutation({
    mutationFn: async (input: StartSessionRolloverInput) => {
      const response = await baseAPI.post<SessionRolloverJob>(
        '/v2/session-rollovers',
        input,
      );
      return response.data;
    },
  });

export const useSessionRolloverJob = (
  jobId: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: rolloverKey(jobId),
    queryFn: async () => {
      const response = await baseAPI.get<SessionRolloverJob>(
        `/v2/session-rollovers/${jobId}`,
      );
      return response.data;
    },
    enabled: options?.enabled !== false && Boolean(jobId),
    retry: (failureCount, error) => {
      if (isMissingSessionRolloverJobError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'processing' ? 1000 : false;
    },
  });

export const useSessionRolloverJobs = () =>
  useQuery({
    queryKey: rolloverKey(),
    queryFn: async () => {
      const response = await baseAPI.get<Array<SessionRolloverJob>>(
        '/v2/session-rollovers',
      );
      return response.data;
    },
    refetchInterval: (query) =>
      query.state.data?.some(
        (job) => job.status === 'queued' || job.status === 'processing',
      )
        ? 2000
        : false,
  });

const useSessionRolloverActionMutation = (action: 'retry' | 'cancel') =>
  useMutation({
    mutationFn: async (jobId: string) => {
      const response = await baseAPI.post<SessionRolloverJob>(
        `/v2/session-rollovers/${jobId}/${action}`,
      );
      return response.data;
    },
    onSuccess: (job) => {
      queryClient.setQueryData(rolloverKey(job.jobId), job);
      void queryClient.invalidateQueries({ queryKey: rolloverKey() });
    },
  });

export const useRetrySessionRollover = () =>
  useSessionRolloverActionMutation('retry');
export const useCancelSessionRollover = () =>
  useSessionRolloverActionMutation('cancel');

export const useDeleteSessionRollover = () =>
  useMutation({
    mutationFn: async (jobId: string) => {
      const response = await baseAPI.delete<{
        deleted: boolean;
        sessionDeleted: boolean;
        warnings: Array<string>;
      }>(`/v2/session-rollovers/${jobId}`);
      return response.data;
    },
    onSuccess: (_result, jobId) => {
      const storedJobId = sessionStorage.getItem(
        ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY,
      );
      if (storedJobId === jobId) {
        sessionStorage.removeItem(ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY);
      }
      void queryClient.invalidateQueries({ queryKey: rolloverKey() });
      void queryClient.invalidateQueries({ queryKey: [KEYS.SESSIONS] });
      void queryClient.invalidateQueries({ queryKey: [KEYS.CLASSES] });
    },
  });
